//#region src/auth/scope/errors.ts
/**
* Thrown by {@link parseScope} / {@link validateScopeArray} when a scope
* string does not match any known grammar (capability, collab, virtual,
* or wildcard).
*
* Carries the offending scope verbatim so route/JWT-layer error
* handlers can echo it to the customer.
*/
var InvalidScope = class extends Error {
	constructor(scope, reason) {
		super(`invalid scope "${scope}": ${reason}`);
		this.scope = scope;
		this.name = "InvalidScope";
	}
};
/**
* Thrown by route guards and engine-local enforcement when a capability
* or collab action is denied for the current request/handle.
*
* `required` names what the caller needed (e.g., "doc.render" or
* "annotations:update"). `context` is an optional label such as "local"
* or "/v1/docs/.../pages/2/render".
*/
var PermissionDenied = class extends Error {
	constructor(required, context) {
		super(`permission denied${context ? ` (${context})` : ""}: ${required}`);
		this.required = required;
		this.context = context;
		this.name = "PermissionDenied";
	}
};
/**
* Thrown at engine-local open time when the supplied scope includes
* collab filters (`:self`, `:group=...`) but the identity claims needed
* to resolve them are missing. Fails loudly at open so the configuration
* mistake is visible immediately instead of producing silent denies at
* every annotation mutation.
*/
var MissingIdentity = class extends Error {
	constructor(scope) {
		super(`scope "${scope}" requires identity claims (user_id and/or groups)`);
		this.name = "MissingIdentity";
	}
};
//#endregion
//#region src/auth/scope/parser.ts
/**
* Closed set of recognized capability strings. Membership is the
* authoritative source of truth — adding a new capability requires
* extending the `DocCapability` union AND adding it here. The parser
* rejects anything outside this set.
*
* Removed legacy names (e.g., `doc.read`, `doc.edit-pages`, `doc.save`)
* are deliberately absent so any JWT carrying them fails at verify
* time with a clear error.
*/
const KNOWN_CAPABILITIES = /* @__PURE__ */ new Set([
	"doc.open",
	"doc.render",
	"doc.text.select",
	"doc.text.copy",
	"doc.text.search",
	"doc.content.copy",
	"doc.download",
	"doc.download.flattened",
	"doc.print",
	"doc.print.high",
	"doc.pages.modify",
	"doc.pages.assemble",
	"doc.forms.read",
	"doc.forms.fill",
	"doc.forms.modify",
	"doc.annotate.read",
	"doc.annotate.modify",
	"doc.metadata.modify",
	"doc.attachments.modify",
	"doc.redact"
]);
/**
* Parse a single scope string into a structured `ParsedScope`.
*
* Recognised shapes:
*   - `"*"`                              → wildcard
*   - `"pdf.permissions"`                → virtual (expands at resolve time)
*   - `"a.b.c..."` (dotted)              → capability
*   - `"entity:action:filter"` (colons)  → collab
*
* Throws {@link InvalidScope} for any string that fails to match one of
* those shapes, or whose capability/entity/action/filter is not in the
* closed enum.
*/
function parseScope(raw) {
	if (raw === "*") return { kind: "wildcard" };
	if (raw === "pdf.permissions") return {
		kind: "virtual",
		name: "pdf.permissions"
	};
	if (raw.includes(":")) return parseCollab(raw);
	return parseCapability(raw);
}
/**
* Validate every entry in a scope array by attempting to parse it.
* Throws on the first invalid string. Discards the parse output —
* callers re-parse at resolution time to keep the array as the
* authoritative payload.
*/
function validateScopeArray(raw) {
	for (const s of raw) parseScope(s);
}
function parseCollab(raw) {
	const idx1 = raw.indexOf(":");
	const idx2 = raw.indexOf(":", idx1 + 1);
	if (idx1 === -1 || idx2 === -1) throw new InvalidScope(raw, "collab scope requires entity:action:filter");
	const entity = raw.slice(0, idx1);
	const action = raw.slice(idx1 + 1, idx2);
	const filterStr = raw.slice(idx2 + 1);
	if (entity !== "annotations") throw new InvalidScope(raw, `unknown collab entity: ${entity}`);
	if (action !== "create" && action !== "update" && action !== "delete" && action !== "set-group" && action !== "*") throw new InvalidScope(raw, `unknown collab action: ${action}`);
	const filter = parseFilter(filterStr, raw);
	if (action === "set-group" && (filter.kind === "self" || filter.kind === "createdBy")) throw new InvalidScope(raw, `set-group only supports :all or :group=<id> filters (got :${filter.kind})`);
	return {
		kind: "collab",
		entity,
		action,
		filter
	};
}
function parseFilter(s, raw) {
	if (s === "all") return { kind: "all" };
	if (s === "self") return { kind: "self" };
	if (s.startsWith("createdBy=")) {
		const v = s.slice(10);
		if (!v) throw new InvalidScope(raw, "createdBy= requires a value");
		return {
			kind: "createdBy",
			userId: v
		};
	}
	if (s.startsWith("group=")) {
		const v = s.slice(6);
		if (!v) throw new InvalidScope(raw, "group= requires a value");
		return {
			kind: "group",
			groupId: v
		};
	}
	throw new InvalidScope(raw, `unknown filter: ${s}`);
}
const CAPABILITY_SHAPE = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;
function parseCapability(raw) {
	if (!CAPABILITY_SHAPE.test(raw)) throw new InvalidScope(raw, "invalid capability name shape");
	if (!KNOWN_CAPABILITIES.has(raw)) throw new InvalidScope(raw, `unknown capability: ${raw}`);
	return {
		kind: "capability",
		name: raw
	};
}
//#endregion
//#region src/auth/scope/resolver.ts
/**
* True iff the given capability is granted by the scope array plus the
* PDF bits visible via `pdf.permissions` expansion.
*
* Wildcard `*` short-circuits to true. Anything not explicitly granted
* (or expanded from `pdf.permissions`) is denied.
*/
function checkCapability(capability, rawScope, pdfBits) {
	const parsed = rawScope.map(parseScope);
	if (parsed.some((s) => s.kind === "wildcard")) return true;
	return expandedCapabilities(parsed, pdfBits).has(capability);
}
/**
* True iff the scope grants AT LEAST ONE of `capabilities`. Convenience
* for routes like `/text` (`doc.text.copy` OR `doc.text.search`) and
* `/geometry` (`doc.text.select` OR `doc.text.search`).
*/
function checkAnyCapability(capabilities, rawScope, pdfBits) {
	return capabilities.some((c) => checkCapability(c, rawScope, pdfBits));
}
/**
* True iff the scope grants the collab action against the target record.
*
* Narrowing model. For each action independently:
*   1. wildcard `*` → allow (global escape hatch)
*   2. if any collab scope applies to this action → NARROW: only those
*      collab filters decide. If none match the target, deny — even if
*      `doc.annotate.modify` is also present. This is what makes
*      `[modify, update:self]` correctly mean "edit own only" rather
*      than "edit anyone via modify-bypass."
*   3. otherwise, if `doc.annotate.modify` is present → allow. This is
*      the broad PDF-bit-derived default for create/update/delete when
*      no per-action collab filter has been written.
*   4. otherwise → deny.
*
* For create: `target` should be built by the caller from JWT identity
* (`{ userId: caller.user_id, groupId: caller.group_id }`). `:self` and
* `:all` then trivially pass; `:group=X` is the meaningful filter (only
* matches when the caller's default group is X).
*
* For update / delete: `target` is the existing row's owner.
*/
function checkCollab(action, target, rawScope, identity, pdfBits) {
	const parsed = rawScope.map(parseScope);
	if (parsed.some((s) => s.kind === "wildcard")) return true;
	const applicableCollab = parsed.filter((s) => s.kind === "collab" && s.entity === "annotations" && (s.action === action || s.action === "*"));
	if (applicableCollab.length > 0) return applicableCollab.some((s) => filterMatches(s.filter, target, identity));
	return expandedCapabilities(parsed, pdfBits).has("doc.annotate.modify");
}
/**
* Compute the full set of granted capabilities after expanding
* `pdf.permissions` and applying implication rules.
*
* Implications applied:
*   - `doc.annotate.modify` implies `doc.annotate.read` (you can't
*     sensibly modify what you can't see).
*   - `doc.forms.modify` implies `doc.forms.fill` and `doc.forms.read`.
*   - `doc.forms.fill`   implies `doc.forms.read`.
*   - any annotation collab scope implies `doc.annotate.read`, because
*     mutation routes need to see the target row to evaluate the
*     collab filter against its current owner.
*
* Does NOT short-circuit on wildcard — callers do that themselves
* before calling this. Returning the expanded set is useful for the
* `/access` response's `effectiveScope` and for advisory UI surfacing.
*/
function expandedCapabilities(parsed, pdfBits) {
	const out = /* @__PURE__ */ new Set();
	let hasAnnotationCollab = false;
	for (const s of parsed) if (s.kind === "capability") out.add(s.name);
	else if (s.kind === "virtual" && s.name === "pdf.permissions") addPdfPermissions(out, pdfBits);
	else if (s.kind === "collab" && s.entity === "annotations") hasAnnotationCollab = true;
	if (out.has("doc.annotate.modify")) out.add("doc.annotate.read");
	if (out.has("doc.forms.modify")) {
		out.add("doc.forms.fill");
		out.add("doc.forms.read");
	}
	if (out.has("doc.forms.fill")) out.add("doc.forms.read");
	if (hasAnnotationCollab) out.add("doc.annotate.read");
	return out;
}
/**
* Convenience wrapper: takes the raw scope array, parses each entry,
* returns the expanded capability set. Exposed for the `/access`
* endpoint and for the SDK helper that surfaces `effectiveScope`.
*/
function expandRawScope(rawScope, pdfBits) {
	return expandedCapabilities(rawScope.map(parseScope), pdfBits);
}
/**
* Test a single collab filter against a target record + the caller's
* identity. Pure function — no side effects, no implicit rules.
*
*   all              → always matches
*   self             → matches if identity.user_id === target.userId
*   createdBy=<X>    → matches if target.userId === X
*   group=<X>        → matches if target.groupId === X
*                      AND identity.groups includes X
*
* The group-membership check on `group=X` prevents a token from
* matching annotations in a group it doesn't belong to, even if the
* target row carries that groupId.
*/
/**
* Authority to assign a specific groupId to an annotation.
*
* Set-group is decoupled from `doc.annotate.modify`. The reasoning:
* capabilities like `modify` describe row access — what kind of write
* you can do to which existing rows — and map to PDF permission bits.
* Set-group is a cloud-only *assignment authority*: which destination
* group can you put an annotation into? There is no PDF-bit
* counterpart, so it doesn't inherit from `modify`.
*
* Resolution order:
*   1. newGroupId === callerDefaultGroupId → true (no real reassignment
*      is happening; the annotation gets the caller's default group)
*   2. wildcard `*` → true (global escape hatch)
*   3. `annotations:set-group:all` → true
*   4. `annotations:set-group:group=<newGroupId>` → true
*   5. `annotations:*:all` or `annotations:*:group=<newGroupId>` → true
*      (action wildcard includes set-group)
*   6. otherwise → false
*
* Note: independent from membership. A user with
* `set-group:group=legal` can assign annotations to the legal group
* even if they're not a member — that's the whole point.
*/
function checkSetGroup(newGroupId, callerDefaultGroupId, rawScope, _pdfBits) {
	if (newGroupId === callerDefaultGroupId) return true;
	const parsed = rawScope.map(parseScope);
	if (parsed.some((s) => s.kind === "wildcard")) return true;
	for (const s of parsed) {
		if (s.kind !== "collab") continue;
		if (s.entity !== "annotations") continue;
		if (s.action !== "set-group" && s.action !== "*") continue;
		if (s.filter.kind === "all") return true;
		if (s.filter.kind === "group" && s.filter.groupId === newGroupId) return true;
	}
	return false;
}
function filterMatches(filter, target, id) {
	switch (filter.kind) {
		case "all": return true;
		case "self": return !!id.user_id && target.userId === id.user_id;
		case "createdBy": return target.userId === filter.userId;
		case "group": return target.groupId === filter.groupId && (id.groups?.includes(filter.groupId) ?? false);
	}
}
/**
* Translate `pdf.permissions` (virtual scope) into the concrete
* capabilities it represents under the current PDF bit configuration.
*
* Always adds `doc.open` and `doc.render` — these are cloud-only
* capabilities with no PDF bit, but `pdf.permissions` is meant to be
* the "give the user a working session" shorthand. Without them, a
* token with just `['pdf.permissions']` would be useless.
*
* Reads are unconditional: ISO 32000 / Acrobat let any reader see
* existing annotations and form values regardless of permission bits.
* Bit 6 governs *writing*, not visibility.
*
* Bit-derived expansions follow ISO 32000:
*   bit 5   → doc.text.{select, copy, search}, doc.content.copy
*   bit 3   → doc.print
*   bit 12  → doc.print.high (requires bit 3 also set)
*   bit 4   → doc.pages.modify, doc.redact, doc.metadata.modify, doc.attachments.modify
*   bit 11  → doc.pages.assemble
*   bit 6   → doc.annotate.modify
*   bit 6/9 → doc.forms.fill
*   bit 6+4 → doc.forms.modify
*
* Note: this same expansion lives in builders.ts as
* `materializePdfPermissions` for SDK-side use. The two MUST stay in
* sync; a test in resolver.test.ts pins them together.
*/
function addPdfPermissions(out, b) {
	out.add("doc.open");
	out.add("doc.render");
	out.add("doc.annotate.read");
	out.add("doc.forms.read");
	if (b.bit5) {
		out.add("doc.text.select");
		out.add("doc.text.copy");
		out.add("doc.text.search");
		out.add("doc.content.copy");
	}
	if (b.bit3) out.add("doc.print");
	if (b.bit12 && b.bit3) out.add("doc.print.high");
	if (b.bit4) {
		out.add("doc.pages.modify");
		out.add("doc.redact");
		out.add("doc.metadata.modify");
		out.add("doc.attachments.modify");
	}
	if (b.bit11) out.add("doc.pages.assemble");
	if (b.bit6) out.add("doc.annotate.modify");
	if (b.bit6 || b.bit9) out.add("doc.forms.fill");
	if (b.bit6 && b.bit4) out.add("doc.forms.modify");
}
//#endregion
export { expandRawScope as a, parseScope as c, MissingIdentity as d, PermissionDenied as f, checkSetGroup as i, validateScopeArray as l, checkCapability as n, expandedCapabilities as o, checkCollab as r, filterMatches as s, checkAnyCapability as t, InvalidScope as u };

//# sourceMappingURL=resolver-DiPMTfcy.js.map